/* A window's sections as cards (Duncan 2026-10-30: Task Properties' sections were "not really separated" -- each a card, and
   "sort of like the pop out thing that we are doing on other spots, but instead of the huge pop out, it just pops up a
   little bit in the other sections blur slight"; his pick: when you point at it). Each section runs from its header to the
   next: the card under the pointer lifts a little and the others blur, until the pointer leaves them (`pop` false: plain
   cards). And the pencil rail (subs/ScrollRail.js) floats beside the window -- "not inside the window, but next to it to the
   left" -- with a dot for each section where it sits, the one under the pointer marked (else the one in view); a click
   glides there. Only while the window scrolls and there's room to its left (a narrow rail, dots only, when there's a little).
   The rail is portaled outside the window; a click on it still stops at the window (React events follow the tree). */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './ui/Icon';
import ScrollRail from './subs/ScrollRail';

const RAIL_W = 124;
const GAP = 14;

const ICONS = { General: 'list', Schedule: 'calendar', Completion: 'pulse', Predecessors: 'arrow-left', 'Dependent Tasks': 'caret-r',
  Options: 'gear', 'SOV Links': 'wallet', Notes: 'pencil', 'What it holds now': 'wallet', "Each subtask's share": 'pulse', 'Fixture lines, by area': 'list', 'The area it pays for': 'map-pin', 'What each bottom row is worth': 'wallet' };
const still = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A section's header, read by SectionCards (renders nothing by itself). */
export function SectionHead() { return null; }
SectionHead.isSectionHead = true;

/** The rail beside the window: where the window's scrolling box sits on screen, and its sections' places down it. */
function useWindowRail(rootRef, count) {
  const boxRef = useRef(null);
  const [rail, setRail] = useState(null); // { left, top, height, narrow, items } or null
  const measure = () => {
    const root = rootRef.current;
    const box = root && root.parentElement;
    if (!box) return;
    boxRef.current = box;
    const r = box.getBoundingClientRect();
    const scrolls = box.scrollHeight > box.clientHeight + 4;
    const room = r.left - GAP;
    if (!scrolls || room < 30) { setRail(null); return; }
    const narrow = room < RAIL_W + 10;
    const cards = [...root.children];
    const items = cards.map((c, i) => ({ id: `s${i}`, label: c.dataset.title || '', tip: c.dataset.title || '', count: '',
      top: Math.round(c.getBoundingClientRect().top - r.top + box.scrollTop) })).filter((it) => it.label);
    const w = narrow ? 30 : RAIL_W;
    const next = { left: Math.round(r.left - GAP - w), top: Math.round(r.top), height: Math.round(box.clientHeight), narrow, w, items };
    setRail((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  };
  useLayoutEffect(measure);
  useEffect(() => {
    const root = rootRef.current;
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (ro && root) { ro.observe(root); if (root.parentElement) ro.observe(root.parentElement); }
    window.addEventListener('resize', measure);
    let n = 0; let raf = 0; // the window settling into place as it opens
    const settle = () => { measure(); if ((n += 1) < 30) raf = requestAnimationFrame(settle); };
    raf = requestAnimationFrame(settle);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); cancelAnimationFrame(raf); };
  }, [count]);
  return { rail, boxRef };
}

export default function SectionCards({ children, rail: withRail = true, railZ = 10001, pop = true }) {
  const [hot, setHot] = useState(null);
  const rootRef = useRef(null);
  const groups = [];
  React.Children.toArray(children).forEach((ch) => {
    if (React.isValidElement(ch) && ch.type && ch.type.isSectionHead) groups.push({ head: ch.props, body: [] });
    else if (groups.length) groups[groups.length - 1].body.push(ch);
    else groups.push({ head: null, body: [ch] });
  });
  const { rail, boxRef } = useWindowRail(rootRef, groups.length);
  return (
    <>
    <div ref={rootRef} onMouseLeave={() => setHot(null)} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 6px 6px', margin: '0 -6px' }}>
      {groups.map((g, i) => {
        const on = hot === i;
        const off = hot != null && !on;
        return (
          <div key={i} data-title={g.head ? g.head.title : ''} onMouseEnter={() => { if (pop) setHot(i); }} style={{
            position: 'relative', zIndex: on ? 2 : 'auto',
            border: '1px solid var(--border-default)', borderRadius: 10, background: 'var(--bg-surface)',
            transition: still ? 'none' : 'transform .18s cubic-bezier(.2,.8,.2,1.2), box-shadow .18s ease, filter .18s ease, opacity .18s ease',
            transform: on && !still ? 'translateY(-3px) scale(1.01)' : 'none',
            boxShadow: on ? '0 10px 28px rgba(0,0,0,.14), 0 2px 6px rgba(0,0,0,.08)' : 'none',
            // Lighter, so a card can still be read past the one in focus (Duncan 2026-10-30, option A).
            filter: off && !still ? 'blur(0.6px)' : 'none',
            opacity: off ? 0.85 : 1,
          }}>
            {g.head && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)', borderRadius: '10px 10px 0 0' }}>
                {ICONS[g.head.title] && <Icon name={ICONS[g.head.title]} size={15} style={{ color: 'var(--text-3)' }} />}
                <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{g.head.title}</span>
                <div style={{ flex: 1 }} />
                {g.head.right}
              </div>
            )}
            <div style={{ padding: '12px 14px 14px' }}>{g.body}</div>
          </div>
        );
      })}
    </div>
    {withRail && rail && rail.items.length > 1 && createPortal(
      // Floating on its own, no card behind it (Duncan: "pure floating"); white ink, the section marked a green pill.
      <div className="task-window-rail" style={{
        position: 'fixed', left: rail.left, top: rail.top, height: rail.height, width: rail.w, zIndex: railZ, display: 'flex',
      }}>
        <style>{`.task-window-rail > div { border-right: none !important; }
`}</style>
        <ScrollRail floating box={boxRef} items={rail.items} title="Sections" narrow={rail.narrow} width={rail.w} activeId={hot != null ? `s${hot}` : null} />
      </div>,
      document.body,
    )}
    </>
  );
}
